import { useTranslation } from '@hopin-team/provider-translation';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { getTicks, scaleBand, scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { defaultStyles, TooltipWithBounds, useTooltip } from '@visx/tooltip';
import React, { useState } from 'react';

import {
  BarChartData,
  formatAxisLabel,
  formatTooltipLabel,
  getIntervalUnit,
  getMaxTickWidth,
  getUpperBound,
  numberFormatter,
} from './helpers';

type Props = {
  chartData: BarChartData;
  tooltipTitle?: string;
};

export const BarChart = ({ chartData, tooltipTitle }: Props) => {
  const { t } = useTranslation();
  const unit = getIntervalUnit(chartData);
  const [keyboardIndex, setKeyboardIndex] = useState(0);
  const currentIndex = Math.min(
    keyboardIndex,
    Math.max(0, chartData.length - 1),
  );
  const chartLabel = `${tooltipTitle || t('analytics.chart')} ${t(
    'analytics.over-time',
  )}`;

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<{ name: string; value: number }>();

  return (
    <ParentSize>
      {(parent: { height: number; width: number }) => {
        // Y axis config
        const maxYTicks = 4;
        const paddingBottom = 35;
        const yMax = parent.height && parent.height - paddingBottom;
        const maxValue = Math.max(...chartData.map(data => data[1]));

        const upperYBound = getUpperBound(maxValue, maxYTicks) || 1;
        const numYTicks = Math.min(upperYBound, maxYTicks);
        const yScale = scaleLinear({
          range: [yMax, 0],
          domain: [0, upperYBound],
        });
        const yTicks = getTicks(yScale, numYTicks);

        // X axis config
        const paddingLeft = getMaxTickWidth(yTicks);
        const xMax = parent.width - paddingLeft;

        const xScale = scaleBand({
          range: [0, xMax],
          domain: chartData.map(data => data[0]),
          padding: 0.4,
        });
        const numXTicks = 6;
        const xTicks = getTicks(xScale, numXTicks);

        const showKeyboardPoint = (index: number) => {
          const point = chartData[index];
          if (!point) return;
          const barX = (xScale(point[0]) || 0) + paddingLeft;
          const barY = yScale(point[1]) ?? 0;
          setKeyboardIndex(index);
          showTooltip({
            tooltipData: { name: point[0], value: point[1] },
            tooltipTop: barY,
            tooltipLeft: barX + xScale.bandwidth() / 2,
          });
        };

        const handleKeyDown = (
          event: React.KeyboardEvent<HTMLInputElement>,
        ) => {
          if (event.key === 'Escape') {
            if (tooltipOpen) {
              event.preventDefault();
              event.stopPropagation();
              hideTooltip();
            }
            return;
          }

          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight' || event.key === 'ArrowUp')
            nextIndex++;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown')
            nextIndex--;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = chartData.length - 1;
          else return;

          event.preventDefault();
          showKeyboardPoint(
            Math.max(0, Math.min(nextIndex, chartData.length - 1)),
          );
        };

        return (
          <div
            data-testid="bar-chart"
            className="relative h-full w-full focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-blue-500"
          >
            <input
              type="range"
              className="sr-only"
              min={0}
              max={chartData.length - 1}
              value={currentIndex}
              aria-label={chartLabel}
              aria-valuetext={
                chartData[currentIndex]
                  ? `${formatTooltipLabel(
                      chartData[currentIndex][0],
                      unit,
                    )}: ${tooltipTitle}, ${chartData[currentIndex][1]}`
                  : undefined
              }
              onFocus={() => showKeyboardPoint(currentIndex)}
              onBlur={hideTooltip}
              onKeyDown={handleKeyDown}
              onChange={event =>
                showKeyboardPoint(Number(event.currentTarget.value))
              }
            />
            <svg aria-hidden="true" className="h-full w-full overflow-visible">
              <Group>
                {chartData.map((data, index) => {
                  const xVal = data[0];
                  const yVal = data[1];
                  const barWidth = xScale.bandwidth();
                  const barHeight = yMax && yMax - (yScale(data[1]) ?? 0);
                  const barX = (xScale(xVal) || 0) + paddingLeft;
                  const barY = yMax - barHeight;

                  return (
                    <Bar
                      key={index}
                      x={barX}
                      y={barY}
                      width={barWidth}
                      height={barHeight}
                      className="fill-blue-500 opacity-40"
                      onMouseOver={e =>
                        showTooltip({
                          tooltipData: { name: xVal, value: yVal },
                          tooltipTop: localPoint(e)?.y,
                          tooltipLeft: localPoint(e)?.x,
                        })
                      }
                      onMouseLeave={hideTooltip}
                    />
                  );
                })}
              </Group>
              <AxisBottom
                scale={xScale}
                top={yMax}
                left={paddingLeft}
                numTicks={numXTicks}
                tickValues={xTicks}
                hideTicks
                strokeWidth={2}
                axisLineClassName="stroke-gray-300"
                tickLabelProps={tick => ({
                  textAnchor: tick === xTicks[0] ? 'start' : 'middle',
                })}
                tickComponent={props => {
                  const formattedAxisLabel = formatAxisLabel(
                    xTicks,
                    props.formattedValue,
                    unit,
                  );

                  return (
                    <svg textAnchor={props.textAnchor}>
                      <text
                        x={props.x}
                        y={props.y}
                        className="font-inter fill-gray-650 text-xs"
                      >
                        <tspan>{formattedAxisLabel.text}</tspan>
                        <tspan x={props.x} dy="1.2em">
                          {formattedAxisLabel.subText}
                        </tspan>
                      </text>
                    </svg>
                  );
                }}
              />
              <AxisLeft
                scale={yScale}
                left={paddingLeft}
                numTicks={numYTicks}
                axisClassName="fill-gray-650"
                axisLineClassName="stroke-gray-300"
                tickLength={4}
                hideTicks
                strokeWidth={2}
                tickFormat={numberFormatter}
                tickLabelProps={() => ({
                  fontSize: 12,
                  textAnchor: 'end',
                  dy: '0.33em',
                  dx: '-.33em',
                })}
                labelClassName="font-inter fill-gray-650"
                labelOffset={10}
              />
            </svg>
            <table className="sr-only">
              <caption>{chartLabel}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('analytics.date')}</th>
                  <th scope="col">{tooltipTitle}</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map(([date, value], index) => (
                  <tr key={`${date}-${index}`}>
                    <td>{formatTooltipLabel(date, unit)}</td>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tooltipOpen && tooltipData && (
              <TooltipWithBounds
                key={Math.random()} // Set to random so it correctly updates with parent bounds
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultStyles,
                  backgroundColor: '#05141f',
                  opacity: 0.7,
                  padding: 16,
                  borderRadius: 8,
                }}
              >
                <div className="flex justify-between gap-4">
                  <div>
                    <p className="font-inter mb-6 text-sm text-white">
                      {formatTooltipLabel(tooltipData.name, unit)}
                    </p>
                    <p className="font-inter text-sm text-white">
                      {tooltipTitle}
                    </p>
                  </div>
                  <div className="font-inter self-end text-sm font-bold text-white">
                    <p>{tooltipData.value}</p>
                  </div>
                </div>
              </TooltipWithBounds>
            )}
          </div>
        );
      }}
    </ParentSize>
  );
};
